import { useEffect } from "react";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router";
import type { LinksFunction } from "react-router";
import { Header } from "@/components/header";
import { Footer } from "@/components/footer";
import { scheduleSentryInit } from "@/lib/sentry-init";
import { buildOrganizationSchema, buildWebSiteSchema } from "@/lib/seo";
import "./app.css";

export const links: LinksFunction = () => [
  { rel: "preload", href: "/fonts/GeistVF.woff2", as: "font", type: "font/woff2", crossOrigin: "anonymous" },
  // GeistMono preload intentionally omitted — font is sub-LCP (eyebrows/chips only) and loads on-demand via @font-face in app.css
  { rel: "dns-prefetch", href: "https://app.loops.so" },
  { rel: "icon", href: "/icon.svg", type: "image/svg+xml" },
  { rel: "icon", href: "/favicon.ico", sizes: "32x32" },
  { rel: "icon", href: "/favicon-192.png", type: "image/png", sizes: "192x192" },
  { rel: "icon", href: "/favicon-512.png", type: "image/png", sizes: "512x512" },
  { rel: "apple-touch-icon", href: "/apple-icon.png" },
];

const SCHEMA_ORG = JSON.stringify([
  buildOrganizationSchema({
    sameAs: [
      "https://www.linkedin.com/company/truspecta/",
      "https://www.linkedin.com/in/nathanlemay/",
    ],
  }),
  buildWebSiteSchema(),
]);

export default function App() {
  useEffect(() => {
    scheduleSentryInit();
  }, []);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {/* Schema.org structured data — hardcoded strings, not user-supplied */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: SCHEMA_ORG }} // safe: hardcoded schema.org string
        />
      </head>
      <body className="flex min-h-dvh flex-col">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
        >
          Skip to content
        </a>
        {/* Cloudflare Web Analytics */}
        {import.meta.env.VITE_CLOUDFLARE_ANALYTICS_TOKEN && (
          <script
            defer
            src="https://static.cloudflareinsights.com/beacon.min.js"
            data-cf-beacon={JSON.stringify({
              token: import.meta.env.VITE_CLOUDFLARE_ANALYTICS_TOKEN,
            })}
          />
        )}
        <Header />
        <main id="main-content" className="flex-1">
          <Outlet />
        </main>
        <Footer />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary() {
  return (
    <html lang="en">
      <head>
        <title>Error | Truspecta</title>
        <Meta />
        <Links />
      </head>
      <body className="flex min-h-dvh flex-col items-center justify-center">
        <h1 className="font-heading text-2xl font-semibold">
          Something went wrong
        </h1>
        <Scripts />
      </body>
    </html>
  );
}
